import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type Address } from '~/types/address';
import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import {
  type CompanyCreatedValues,
  CompanyObject,
} from '~/models/masterdata/Company';
import { type ParentOrganisationalGroup } from '~/models/masterdata/OrganisationalGroup';

import { queryKeysCompany } from './queryKeys';

type CompanyLegacy = {
  address: Address;
  companyAccountId: UUID;
  companyId: UUID;
  id: UUID;
  name: string;
  orgUnitPaths: UUID[][];
  orgUnits: UUID[];
  parentOrganizationalUnitPaths: UUID[];
  parentOrganizationalUnits: ParentOrganisationalGroup[];
};

type CompanyLegacyResponse = {
  companies: readonly CompanyLegacy[];
};

/**
 * Fetches companies legacy data from the API.
 *
 * @returns The company data if successful, null otherwise.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_all_companies_company_all_get
 */
export const fetchCompaniesLegacy = async () => {
  const { companies } = await vestigasApi
    .get(ENDPOINT.COMPANY.GET_ALL_LEGACY())
    .json<CompanyLegacyResponse>();

  return companies.map((item) => CompanyObject.create(item));
};

// This query uses an deprecated endpoint for fetching ALL companies ever, which is not technically possible for large accounts.
export const useQueryCompaniesLegacy = <TSelected = CompanyCreatedValues[]>(
  options?: Omit<
    UseQueryOptions<CompanyCreatedValues[], Error, TSelected>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<CompanyCreatedValues[], Error, TSelected>({
    queryFn: async () => fetchCompaniesLegacy(),
    queryKey: queryKeysCompany.getAllLegacy(),
    ...options,
  });
